import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { getAvailableTimeSlots, TimeSlot as ApiTimeSlot } from '@/services/bookingService'; // Assuming this service exists/will be created
import { Alert, AlertDescription } from "@/components/ui/alert"; // For error display

// Define the structure for a time slot object based on the error and logs
interface TimeSlot {
  time: string;
  isAvailable: boolean; // We might not use isAvailable here, but it's good practice to type it
}

interface TimeSlotSelectorProps {
  profileId: string; // Added profileId
  selectedDate: Date | undefined;
  selectedSlot: string | null;
  onSlotSelect: (slot: string | null) => void; // Allow setting null
  t: (key: string) => string;
}

export const TimeSlotSelector: React.FC<TimeSlotSelectorProps> = ({ 
  profileId,
  selectedDate,
  selectedSlot,
  onSlotSelect,
  t
}) => {
  // Update state to hold TimeSlot objects instead of strings
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSlots = useCallback(async (date: Date, escortId: string) => {
    setIsLoading(true);
    setError(null);
    onSlotSelect(null); // Reset selection when date changes
    try {
      // Call the service function (needs implementation)
      // Ensure the service returns objects matching the TimeSlot interface or adapt here
      const slots: ApiTimeSlot[] = await getAvailableTimeSlots(escortId, date, 60); // Assuming 60 is duration
      // Map the API response to the local TimeSlot interface if necessary
      // For now, we assume ApiTimeSlot has at least a 'time' property compatible with string.
      // If ApiTimeSlot structure differs significantly, mapping is needed.
      // Example mapping (if ApiTimeSlot was just strings): 
      // const formattedSlots = slots.map(s => ({ time: s, isAvailable: true }));
      // setAvailableSlots(formattedSlots);
      
      // Assuming getAvailableTimeSlots returns { time: string; isAvailable: boolean }[] directly
      // If it returns something else (like just strings), adapt the mapping above.
      // The error suggests it returns objects with 'time' and 'isAvailable' keys.
      setAvailableSlots(slots); 
    } catch (err) {
      console.error("Error fetching time slots:", err);
      setError(t('profile:error_fetching_slots')); // Translate error
      setAvailableSlots([]); // Clear slots on error
    } finally {
      setIsLoading(false);
    }
  }, [t, onSlotSelect]);

  useEffect(() => {
    if (selectedDate && profileId) {
      // Ensure date is at the start of the day for consistent caching/querying if needed
      const startOfDay = new Date(selectedDate);
      startOfDay.setHours(0, 0, 0, 0);
      fetchSlots(startOfDay, profileId);
    } else {
      setAvailableSlots([]); // Clear slots if no date selected
      setError(null);
      setIsLoading(false);
    }
  }, [selectedDate, profileId, fetchSlots]);

  return (
    <div>
      <h3 className="text-sm font-medium mb-3">{t('profile:select_time')}</h3>
      <div className="bg-white rounded-md border p-4 overflow-y-auto min-h-[12rem]"> {/* Adjusted min-height */} 
        {isLoading ? (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
            {t('common:loading')}
          </div>
        ) : error ? (
          <Alert variant="destructive" className="my-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : !selectedDate ? (
          <div className="flex items-center justify-center h-full text-muted-foreground py-4">
            {t('profile:select_date_first')}
          </div>
        ) : availableSlots.length > 0 ? (
          <div className="grid grid-cols-3 gap-2">
            {availableSlots.map((slot) => (
              <Button
                // Use slot.time as the key, assuming time is unique per date
                key={slot.time} 
                // Compare selectedSlot with slot.time
                variant={selectedSlot === slot.time ? "default" : "outline"} 
                // Pass slot.time (string) to the handler
                onClick={() => onSlotSelect(slot.time)} 
                // Update className condition
                className={`text-sm h-10 ${selectedSlot === slot.time ? 'bg-pink-500 hover:bg-pink-600 text-white' : 'hover:bg-pink-50 border-pink-200'}`}
                size="sm"
              >
                {/* Display slot.time */}
                {slot.time} 
              </Button>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-muted-foreground py-4">
            {t('profile:no_slots_available')} {/* Message for no slots */}
          </div>
        )}
      </div>
    </div>
  );
}; 